<template>
	<div class="affairs-list" ref="scrollContainer">
		<div
			v-for="item in groupedTasks"
			:key="item.group_id"
			class="affairs-list__item"
		>
			<template v-if="!hideGroup(item)">
				<div v-if="item.date" class="affairs-list__date">
					<p>{{ getItemDate(item) }}</p>
				</div>

				<template v-if="item.tasks">
					<div
						v-if="item.tasks.length > 1"
						:class="{
							['tasks_marked']: isMarked(item),
							['tasks_opened']: openedGroupId === item.group_id
						}"
						class="tasks"
					>
						<div class="tasks__head">
							<b class="tasks__head-order">
								<template v-if="item.order">
									{{ $t("order") }} № {{ item.order.id }}
								</template>

								<template v-else>
									{{ $t("group") }} № {{ item.group_id }}
								</template>
							</b>

							<div class="tasks__head-actions">
								<div class="tasks__head-chat">
									<feather-icon
										icon="MessageSquareIcon"
										class="tasks__head-icon"
										@click="openCommentsChat(item)"
									></feather-icon>
								</div>

								<div class="tasks__head-count">
									{{ item.tasks.length }}
								</div>
							</div>
						</div>

						<div
							v-if="openedGroupId !== item.group_id"
							class="tasks__description"
						>
							{{ item.description }}
						</div>

						<div v-else class="tasks__container">
							<div
								v-for="task in item.tasks"
								:key="task.id"
								class="tasks__item"
							>
								<AffairListItem
									:data="task"
									isGroupTask
									@assignToManager="onAssignToManager"
									@editTask="onEditTask"
								/>
							</div>
						</div>

						<div
							:class="{
								['tasks__trigger_opened']:
									openedGroupId === item.group_id
							}"
							class="tasks__trigger"
							@click="openGroup(item.group_id)"
						>
							<feather-icon
								icon="ChevronDownIcon"
								class="tasks__trigger-icon"
							></feather-icon>
						</div>
					</div>

					<AffairListItem
						v-else
						:data="getItem(item)"
						@assignToManager="onAssignToManager(item.tasks[0])"
						@editTask="onEditTask(item.tasks[0])"
						@openComments="openCommentsChat(item)"
					/>
				</template>
			</template>
		</div>

		<div v-if="hasNextPage" ref="endLine" class="text-center my-1">
			loading...
		</div>

		<div
			v-if="!groupedTasks.length && !isLoading"
			class="affairs-list__empty"
		>
			<h3>{{ $t("emptyList") }}</h3>
		</div>
	</div>
</template>

<script>
import AffairListItem from "@/components/affairs/AffairListItem";

import moment from "moment";
import { groupBy, get, flatMap, orderBy } from "lodash";
import date from "@/assets/configs/date.js";

export default {
	name: "AffairsList",

	components: {
		AffairListItem
	},

	props: {
		taskList: {
			type: Array,
			default: () => []
		},

		filter: {
			type: String,
			default: ""
		},

		totalTasksCount: {
			type: Number,
			default: 0
		}
	},

	data() {
		return {
			dateNow: moment(new Date()).format("YYYY-MM-DD"),
			observer: null,
			openedGroupId: null
		};
	},

	watch: {
		hasNextPage: {
			handler(newVal) {
				this.$nextTick(() => {
					if (this.observer && this.$refs.endLine) {
						this.observer.observe(this.$refs.endLine);
					}
				});
			},

			immediate: true
		},

		filter() {
			this.openedGroupId = null;
		}
	},

	computed: {
		hasNextPage() {
			return this.$store.getters["calendar/hasNextPage"];
		},

		groupedTasks() {
			let groups = groupBy(this.taskList, o =>
				moment(get(o, "date_time", "")).format("YYYY-MM-DD")
			);

			let arr = [];
			for (const key in groups) {
				arr.push({
          date: key
        });
				arr.push(...groups[key]);
			}

			return arr;
		},

		isLoading() {
			return this.$store.getters["calendar/getIsLoadingEvents"];
		}
	},

	methods: {
		hideGroup(item) {
			const currentDate = moment(new Date()).format("YYYY-MM-DD");

			if (!item.tasks) {
				return false;
			}
			const hasCompletedTasks = item.tasks.every(el => el.is_completed);

			if (
				this.filter === "byNow" &&
				moment(item.date_time).format("YYYY-MM-DD") !== currentDate &&
				hasCompletedTasks
			) {
				return true;
			}

			return (
				(this.filter === "current" || this.filter === "byNow") &&
				hasCompletedTasks
			);
		},

		getItemDate(item) {
			const dateMoment = moment(item.date);
			const weekDay = this.$t(date.days[dateMoment.day()]);
			const day = dateMoment.format("DD");
			const month = this.$t(date.months[dateMoment.month()] + "Genitive");
			const year = dateMoment.format("YYYY");
			return `${weekDay}, ${day} ${month} ${year}`;
		},

		getItem(item) {
			if (item.date) {
				return item;
			}

			return {
				...item.tasks[0]
			};
		},

		openGroup(id) {
			if (id === this.openedGroupId) {
				this.openedGroupId = null;
				return;
			}

			this.openedGroupId = id;
		},

		openCommentsChat(item) {
			if (!item) return;

			this.$emit("openCommentsChat", item);
		},

		isMarked(item) {
			const taskDate = item.date_time;
			const currentDate = moment(new Date()).format("YYYY-MM-DD");
			return currentDate > taskDate;
		},

		onAssignToManager(e) {
			this.$emit("assignToManager", e);
		},

		onEditTask(e) {
			this.$emit("editTask", e);
		},

		async infiniteScroll([{ isIntersecting, target }]) {
			if (!isIntersecting) {
				return;
			}

			if (!(target instanceof HTMLElement)) {
				return;
			}

			const ul = target.offsetParent;
			const scrollTop = target.offsetParent.scrollTop;

			if (this.observer) {
				this.observer.disconnect();
			}

			const callback = err => {
				this.$nextTick(() => {
					if (ul) {
						ul.scrollTop = scrollTop ? scrollTop : 0;
					}
				});

				if (this.hasNextPage && this.observer && this.$refs.endLine) {
					this.observer.observe(this.$refs.endLine);
				}
			};

			this.$emit("load-events", callback);
		}
	},

	mounted() {
		this.$nextTick(() => {
			this.observer = new IntersectionObserver(this.infiniteScroll);

			setTimeout(() => {
				if (this.observer && this.$refs.endLine) {
					this.observer.observe(this.$refs.endLine);
				}
			}, 1000);
		});
	}
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/vuexy/_variables.scss";
.affairs-list {
	width: 100%;
	overflow-y: auto;
	display: flex;

	&__date {
		padding: 1rem;
	}

	&__empty {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
	}
}

.tasks {
	display: grid;
	border-top: 1px solid #ddd;
	background-color: #f1f1f166;
	align-items: center;
	grid-template-areas:
		"head head"
		"description trigger"
		"container container";

	&_marked {
		background-color: #f4a9a9;

		.tasks__head-icon {
			svg {
				fill: white;
				stroke: white;
			}
		}
	}

	&_opened {
		grid-template-areas:
			"head head"
			"container container"
			". trigger";
	}

	&__head {
		grid-area: head;
		display: flex;
		align-items: center;
		justify-content: space-between;
		grid-column-gap: 10px;
		padding: 10px;
		font-size: 14px;

		&-actions {
			display: flex;
			align-items: center;
			grid-column-gap: 10px;
		}

		&-chat {
			display: flex;
		}

		&-count {
			border-radius: 4px;
			border: 1px solid white;
			background: $warning;
			color: white;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 1px 7px;
		}

		&-icon {
			cursor: pointer;

			svg {
				width: 30px;
				height: 30px;
				fill: white;
				stroke: $grey;
			}
		}
	}

	&__description {
		grid-area: description;
		padding: 0 10px;
	}

	&__container {
		grid-area: container;
	}

	&__trigger {
		grid-area: trigger;
		display: flex;
		justify-content: flex-end;
		overflow: hidden;
		align-self: flex-end;

		&_opened {
			.tasks__trigger-icon {
				transform: rotate(180deg);
			}
		}

		&-icon {
			cursor: pointer;
			transition: transform ease 0.25s;

			svg {
				width: 50px;
				height: 50px;
				fill: white;
				stroke: none;
			}
		}
	}
}
</style>
